import { useState, useEffect } from 'react'
import { Chip, IconButton, Tooltip, Box } from '@mui/material'
import VisibilityIcon from '@mui/icons-material/Visibility'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import PublishIcon from '@mui/icons-material/Publish'
import UnpublishedIcon from '@mui/icons-material/Unpublished'
import axios from 'axios'
import Swal from 'sweetalert2'
import ViewBlogModal from '../../ViewBlogModal'
import EditBlogModal from '../../EditBlogModal'
import { useAppServices } from 'hook/services'



const AppData = () => {
  const [blogs, setBlogs] = useState([])
  const [loader, setLoader] = useState(false)
  const [search, setSearch] = useState('')
  const [pagination, setPagination] = useState({ page: 1, limit: 10, totalPages: 0, totalUsers: 0 })
  const [dataTableData, setDataTableData] = useState({ columns: [], rows: [] })
  const AppService = useAppServices()
  const fetchBlogs = async (page = 1, limit = 10) => {
    setLoader(true)
    try {
      const query = `page=${page}&limit=${limit}`
      const { response } = await AppService.aicollection.get({ query })
      setBlogs(response.collections || [])
      setPagination({
        page: response.meta?.currentPage || page,
        limit: response.meta?.limit || limit,
        totalPages: response.meta?.totalPages || 0,
        totalUsers: response.meta?.total || 0,
      })
    } catch (err) {
      console.error('Failed to fetch blogs', err)
    } finally {
      setLoader(false)
    }
  }

  const handleTogglePublish = async (blog) => {
    const newStatus = blog?.status === 'published' ? 'draft' : 'published'
    try {
      const payload = { id: blog?._id, status: newStatus }
      await AppService.aicollection?.update_status({ payload })
      fetchBlogs()
    } catch (err) {
      Swal.fire('Error', 'Failed to update status', 'error')
    }
  }

  const handleDelete = (id) => {
    Swal.fire({
      title: 'Delete Collection?',
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const payload = {
            id
          }
          await AppService.aicollection.delete({ payload })
          Swal.fire('Deleted!', 'Collection  has been deleted.', 'success')
          fetchBlogs()
        } catch (err) {
          Swal.fire('Error', 'Failed to delete blog', 'error')
        }
      }
    })
  }

  useEffect(() => {
    fetchBlogs()
  }, [])


  const handleRefresh = () => fetchBlogs(pagination.page, pagination.limit, search)
  const handlePageChange = (page) => fetchBlogs(page, pagination.limit, search)
  const handleLimitChange = (limit) => fetchBlogs(1, limit, search)
  const handleSearch = () => fetchBlogs(1, pagination.limit, search)



  useEffect(() => {
    const columns = [
      { Header: 'Title', accessor: 'title', width: '25%' },
      { Header: 'Slug', accessor: 'slug', width: '20%' },
      { Header: 'Author', accessor: 'author', width: '12%' },
      { Header: 'Status', accessor: 'status', width: '10%' },
      { Header: 'Published Date', accessor: 'publishedDate', width: '15%' },
      { Header: 'Actions', accessor: 'actions', width: '18%', disableSortBy: true },
    ]

    const rows = blogs.map((blog) => ({
      title: (
        <Box sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          <Tooltip title={blog.title}>
            <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>{blog.title}</span>
          </Tooltip>
        </Box>
      ),
      slug: (
        <span style={{ fontSize: '0.8rem', color: '#666', fontFamily: 'monospace' }}>
          {blog.slug}
        </span>
      ),
      author: <span style={{ fontSize: '0.875rem' }}>{blog.author || 'Aloca Team'}</span>,
      status: (
        <Chip
          label={blog.status === 'published' ? 'Published' : 'Draft'}
          size="small"
          sx={{
            backgroundColor: blog.status === 'published' ? '#e8f5e9' : '#fff3e0',
            color: blog.status === 'published' ? '#2e7d32' : '#e65100',
            fontWeight: 600,
            fontSize: '0.75rem',
            border: `1px solid ${blog.status === 'published' ? '#a5d6a7' : '#ffcc80'}`,
          }}
        />
      ),
      publishedDate: (
        <span style={{ fontSize: '0.8rem', color: '#555' }}>
          {blog.publishedDate ? new Date(blog.publishedDate).toLocaleDateString('en-AU', {
            day: '2-digit', month: 'short', year: 'numeric'
          }) : '—'}
        </span>
      ),
      actions: (
        <Box display="flex" gap={0.5}>
          <ViewBlogModal blog={blog} />
          <EditBlogModal blog={blog} handleRefresh={handleRefresh} />
          <Tooltip title={blog.status === 'published' ? 'Unpublish' : 'Publish'}>
            <IconButton
              size="small"
              onClick={() => handleTogglePublish(blog)}
              sx={{
                color: blog.status === 'published' ? '#e65100' : '#2e7d32',
                '&:hover': { backgroundColor: blog.status === 'published' ? '#fff3e0' : '#e8f5e9' },
              }}
            >
              {blog.status === 'published' ? (
                <UnpublishedIcon fontSize="small" />
              ) : (
                <PublishIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>

          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={() => handleDelete(blog._id)}
              sx={{ color: '#c62828', '&:hover': { backgroundColor: '#ffebee' } }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    }))

    setDataTableData({ columns, rows })
  }, [blogs])

  return {
    dataTableData,
    handleRefresh,
    loader,
    pagination,
    handlePageChange,
    handleLimitChange,
    search,
    setSearch,
    handleSearch,
  }
}

export default AppData
